import * as echarts from "echarts";
import type { EChartsReactProps } from "echarts-for-react";
// @ts-expect-error - javascript-color-gradient doesn't have type definitions
import Gradient from "javascript-color-gradient";
import React from "react";

import worldMapGeo from "../../media/maps/custom.geo.json";
import SafeECharts from "./SafeECharts";

// worldMapGeo is imported as a JSON module compatible with ECharts registerMap
// registerMap accepts GeoJSON as a string; stringify to satisfy the type constraint
echarts.registerMap("world", JSON.stringify(worldMapGeo));

export interface MapDataItem {
  id: string;
  value?: number;
}

interface MapGraphProps extends Omit<EChartsReactProps, "option"> {
  data?: MapDataItem[];
  className?: string;
  isLoading?: boolean;
  style?: React.CSSProperties;
}

interface GeoFeature {
  properties: {
    name: string;
    iso_a2: string;
  };
}

// Data demo: {id: string, value: number} ||||| The id is iso_a2
const MapGraph: React.FC<MapGraphProps> = ({ data = [], className, isLoading, style, ...EChartsProps }) => {
  const geoFeatures: GeoFeature[] = Array.isArray(worldMapGeo.features) ? worldMapGeo.features : [];
  const dataParsed = geoFeatures.map(({ properties }) => {
    const value = data.find(({ id }) => id === properties.iso_a2)?.value || undefined;
    return {
      name: properties.name,
      value: value,
    };
  });

  const primaryColor = getComputedStyle(document.documentElement).getPropertyValue("--primary").trim();
  const defaultColor = getComputedStyle(document.documentElement).getPropertyValue("--default").trim();

  const maxValue = Math.max(...dataParsed.map((d) => d?.value || 0), 0);

  return (
    <SafeECharts
      {...EChartsProps}
      className={className}
      style={style}
      option={{
        tooltip: {
          trigger: "item",
          formatter: ({ data: tooltipData }: { data: { name: string; value?: number } }) =>
            `${tooltipData.name}: ${tooltipData.value || 0}`,
        },
        visualMap: {
          show: !isLoading,
          left: "right",
          min: 0,
          max: maxValue,
          inRange: {
            color: new Gradient().setColorGradient(primaryColor, defaultColor).setMidpoint(10).getColors(),
          },
          text: ["High", "Low"],
          calculable: true,
        },
        series: [
          {
            emphasis: {
              itemStyle: {
                areaColor: defaultColor,
                color: defaultColor,
              },
              label: {
                show: false,
              },
            },
            name: "Clients",
            type: "map",
            roam: true,
            map: "world",
            zoom: 5,
            center: ["0.7%", "13%"],
            data: dataParsed,
          },
        ],
      }}
    />
  );
};

export default MapGraph;
